import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableRow,
    useTheme
} from '@material-ui/core';
import { truncate } from 'lodash';
import React from 'react';

import { PageDialogLink } from '../common/page-dialog-link';
import { fullDateTime } from '../common/timestamp';
import { JobCandidatesBoard } from '../containers/job-candidates-board';
import { JobTitle } from '../containers/job-title';
import { PersonName } from '../containers/person-name';
import {
    AgentEmailMatches as AgentEmailMatchesQuery,
    MarkAgentEmailMatchReviewed
} from '../graphql/queries/agent-email-matches';
import { GetCommunication } from '../graphql/queries/communications';
import { useAgent } from '../hooks/use-agent';
import { useSlides } from '../hooks/use-candidate-slides';
import { useReduxState } from '../hooks/use-redux';
import { Communication } from '../state';
import { CommunicationDetails } from './communication-details';
import { CellValue, DataPanel, DataPanelColumn, dataPanelContainerStyles, selectorStyles } from './data-panel';

const dialogStyles = css`
    .email-details-container {
        border: none;
        margin: 24px 0;
    }
`;

type Column = 'index' | 'message' | 'account' | 'received' | 'outcome' | 'candidate';

interface MessageRow extends Communication {
    index: number;
}

const pageSize = 50;

const subjectMaxLength = 50;

const columns: Array<DataPanelColumn<Column>> = [
    { id: 'index', label: '#' },
    { id: 'message', label: 'Message' },
    { id: 'account', label: 'Account' },
    { id: 'received', label: 'Received' },
    { id: 'outcome', label: 'Outcome' },
    { id: 'candidate', label: 'Candidate' }
];

const outcomeLabels: Record<string, string> = {
    bounce: 'Bounce',
    ignore: 'Ignore',
    jobSeekerInbound: 'Job-seeker inbound',
    match: 'Match',
    unsure: 'Unsure'
};

const getOutcome = (message: Communication) => {
    const agent = message.matchData?.agentMatch;

    return agent?.status === 'pending' ? 'Pending' : outcomeLabels[agent?.outcome] || 'No outcome';
};

const getState = (message: Communication) => {
    if (message.matchData?.ignored) {
        return message.matchData.forwardedTo ? 'Forwarded' : 'Ignored';
    }

    if (message.matchData?.error) return 'Needs attention';

    return message.personIds?.length && message.jobIds?.length ? 'Matched' : 'Unmatched';
};

const getCellValue =
    (column: Column) =>
    (message: MessageRow): CellValue => {
        switch (column) {
            case 'index':
                return message.index;

            case 'message':
                return message.subject || '(No subject)';

            case 'account':
                return message.account;

            case 'received':
                return Number(message.internalDate);

            case 'outcome':
                return getOutcome(message);

            default:
                return null;
        }
    };

export const AgentEmailMatches: React.FC = () => {
    const theme = useTheme();

    const { onOpen: openAgent, onSelectConversation } = useAgent();

    const { setList } = useSlides();

    const blacklist = useReduxState((state) => state.appConstants.constants.emailContentBlacklistedDomains);

    const [selected, setSelected] = React.useState<Communication | null>(null);

    const [page, setPage] = React.useState(0);

    const [reviewStatus, setReviewStatus] = React.useState('notReviewed');

    const { data, loading, refetch } = useQuery<{
        messages: Communication[];
        total: { aggregate: { count: number } };
    }>(AgentEmailMatchesQuery, {
        variables: {
            limit: pageSize,
            offset: page * pageSize,
            where: reviewStatus === 'all' ? {} : { reviewed: { _eq: reviewStatus === 'reviewed' } }
        }
    });

    const [markReviewed, { loading: reviewing }] = useMutation<{}, { account: string; messageId: string }>(
        MarkAgentEmailMatchReviewed
    );

    const total = data?.total.aggregate.count;

    const messages = data?.messages.map((message, index) => ({ ...message, index: page * pageSize + index + 1 }));

    React.useEffect(() => {
        if (loading || total === undefined) return;

        const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
        if (page > lastPage) setPage(lastPage);
    }, [loading, page, total]);

    const details = useQuery<{ message: Communication | null }>(GetCommunication, {
        skip: !selected,
        variables: {
            account: selected?.account,
            messageId: selected?.messageId
        }
    });

    const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

    const handleChangeReviewStatus = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        setReviewStatus(value);
        setPage(0);
        refetch({ offset: 0, where: value === 'all' ? {} : { reviewed: { _eq: value === 'reviewed' } } });
    };

    const renderSummary = () => (
        <TablePagination
            component="div"
            count={data?.total.aggregate.count ?? 0}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            onChangePage={handleChangePage}
        />
    );

    const handleSelect = (message: Communication) => () => setSelected(message);

    const handleClose = () => {
        if (reviewing) return;

        setSelected(null);
    };

    const handleMarkReviewed = async () => {
        if (!selected || reviewing) return;

        await markReviewed({ variables: { account: selected.account, messageId: selected.messageId } });
        handleClose();
        await refetch();
    };

    const handleOpenPerson = (personId: string, jobId?: string) => (event: React.MouseEvent) => {
        event.preventDefault();

        handleClose();

        const candidate = { personId, jobId };

        setList([candidate], candidate);
    };

    const renderMatch = (personIds: string[], jobIds: string[]) => {
        if (!personIds?.length && !jobIds?.length) return null;

        if (personIds?.length) {
            const people = personIds.flatMap((personId) =>
                (jobIds?.length ? jobIds : [undefined]).map((jobId) => {
                    const job = jobId ? <JobTitle id={jobId} /> : null;

                    const url = jobId ? `/job/${jobId}/board/candidate/${personId}` : `/person/${personId}`;

                    return (
                        <div key={`${personId}/${jobId || ''}`}>
                            <a href={url} onClick={handleOpenPerson(personId, jobId)}>
                                <PersonName id={personId} />
                                {job}
                            </a>
                        </div>
                    );
                })
            );

            return <>{people}</>;
        }

        const jobs = jobIds.map((jobId) => (
            <div key={jobId}>
                <PageDialogLink Component={JobCandidatesBoard} componentProps={{ jobId }} url={`/job/${jobId}/board`}>
                    <JobTitle id={jobId} />
                </PageDialogLink>
            </div>
        ));

        return <>{jobs}</>;
    };

    const handleOpenConversation = (conversationId: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        onSelectConversation(conversationId);
        openAgent();
    };

    const renderOutcome = (message: Communication) => {
        const conversationId = message.matchData?.agentMatch?.conversationId;

        const outcome = (
            <>
                <div>{getState(message)}</div>
                <small>{getOutcome(message)}</small>
            </>
        );

        return conversationId ? (
            <div className="popover-link" onClick={handleOpenConversation(conversationId)}>
                <a href={`/orbit/chat/${conversationId}`} style={{ color: 'inherit' }}>
                    {outcome}
                </a>
            </div>
        ) : (
            outcome
        );
    };

    const getCellRenderer = {
        candidate: (_: CellValue, message: Communication) => {
            if (message.personIds?.length !== 1 || message.jobIds?.length !== 1) return null;

            return (
                <div className="popover-link" onClick={handleOpenPerson(message.personIds[0], message.jobIds[0])}>
                    <div>
                        <PersonName id={message.personIds[0]} />
                    </div>
                    <JobTitle id={message.jobIds[0]} />
                </div>
            );
        },

        message: (value: CellValue, message: Communication) => (
            <div className="popover-link" onClick={handleSelect(message)}>
                <div title={String(value)}>{truncate(String(value), { length: subjectMaxLength })}</div>
                <div className="secondary">{message.sender}</div>
            </div>
        ),

        outcome: (_: CellValue, message: Communication) => renderOutcome(message),

        received: (value: CellValue) => <span className="no-space-wrap">{fullDateTime(Number(value))}</span>
    };

    const detail = details.data?.message;

    const agent = detail?.matchData?.agentMatch;

    const effects = detail?.sideEffectsData?.data;

    const recordedEffects =
        effects && Object.keys(effects).length ? (
            <>
                <div>{fullDateTime(Number(detail.sideEffectsData.timestamp))}</div>
                <pre>{JSON.stringify(effects, null, 2)}</pre>
            </>
        ) : (
            'None recorded'
        );

    const identifiers = detail ? (
        <pre>
            {[`Account: ${detail.account}`, `Message: ${detail.messageId}`, `Thread: ${detail.threadId}`].join('\n')}
        </pre>
    ) : null;

    const fields: Array<[string, React.ReactNode]> = agent
        ? [
              ['Outcome', renderOutcome(detail)],
              ['Reason details', agent.reason],
              ['Server refusal', agent.refusal],
              ['Forwarded to', detail.matchData.forwardedTo],
              ['Current match', renderMatch(detail.personIds, detail.jobIds)],
              ['Proposed match', renderMatch(agent.personId ? [agent.personId] : [], agent.jobId ? [agent.jobId] : [])],
              [
                  'Sender is candidate',
                  agent.senderIsCandidate === undefined ? null : agent.senderIsCandidate ? 'Yes' : 'No'
              ],
              ['Failed recipient', agent.failedRecipient],
              [
                  'Manually edited',
                  detail.matchData.manualOverrideJobIds || detail.matchData.manualOverridePersonIds ? 'Yes' : null
              ]
          ]
        : [];

    if (detail) {
        fields.push(['Recorded side effects', recordedEffects], ['Message identifiers', identifiers]);
    }

    const decisionFields = fields
        .filter(([, value]) => value != null)
        .map(([label, value]) => (
            <TableRow key={label}>
                <TableCell component="th" scope="row">
                    {label}
                </TableCell>
                <TableCell>{value}</TableCell>
            </TableRow>
        ));

    const decision = detail ? (
        <Table size="small">
            <TableBody>{decisionFields}</TableBody>
        </Table>
    ) : null;

    let detailContent: React.ReactNode = null;

    if (details.loading) {
        detailContent = <CircularProgress aria-label="Loading email" />;
    } else if (detail) {
        const communication = (
            <CommunicationDetails
                key={`${detail.account}/${detail.messageId}`}
                data={{ ...detail, internalDate: Number(detail.internalDate) }}
                personId={detail.personIds?.[0]}
                actions={[]}
                handleClose={handleClose}
                fromCrossSubmit={false}
                emailContentBlacklistedDomains={blacklist || ''}
            />
        );

        detailContent = (
            <>
                {decision}
                {communication}
            </>
        );
    }

    const reviewButton = (
        <Button
            onClick={handleMarkReviewed}
            disabled={reviewing || details.loading || detail?.matchData?.agentMatch?.status !== 'complete'}
        >
            {reviewing ? 'Saving review...' : 'Mark reviewed'}
        </Button>
    );

    const dialog = selected ? (
        <Dialog open={true} onClose={handleClose} maxWidth="lg" fullWidth={true} css={dialogStyles}>
            <DialogTitle>Email review</DialogTitle>
            <DialogContent dividers={true}>{detailContent}</DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={reviewing}>
                    Close
                </Button>
                {reviewButton}
            </DialogActions>
        </Dialog>
    ) : null;

    const reviewSelector = (
        <Select
            value={reviewStatus}
            onChange={handleChangeReviewStatus}
            variant="outlined"
            inputProps={{ 'aria-label': 'Review status' }}
        >
            <MenuItem value="notReviewed">Not reviewed</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="all">All messages</MenuItem>
        </Select>
    );

    return (
        <div css={dataPanelContainerStyles(theme)}>
            <div className="selectors">
                <div css={selectorStyles}>{reviewSelector}</div>
            </div>
            <div className="data-panel">
                <DataPanel
                    columns={columns}
                    data={loading ? undefined : messages || []}
                    getCellValue={getCellValue}
                    getCellRenderer={getCellRenderer}
                    pageSize={pageSize}
                    hidePagination={true}
                    renderSummary={data?.total.aggregate.count > pageSize ? renderSummary : undefined}
                />
            </div>
            {dialog}
        </div>
    );
};
